import { createFileRoute, useRouter } from '@tanstack/react-router'
import { VaultMessages } from '../components/VaultMessages.tsx'
import { fetchHierarchy } from '../lib/hierarchy-api.ts'
import { withSessionRedirect } from '../lib/require-session.ts'
import { useAutoRefresh } from '../lib/use-auto-refresh.ts'
import {
  getTenantKey,
  listPrincipals,
  listVaultMessages,
} from '../lib/vault-api.ts'
import { groupIntoThreads } from '../lib/vault-message-threads.ts'
import { sessionUserId, viewerPrincipalFrom } from '../lib/vault-viewer.ts'

/**
 * `/vault/messages`.
 *
 * One page of the root's correspondence, grouped into threads in the
 * browser (`groupIntoThreads`) rather than by a thread endpoint that
 * does not exist: every row already carries `threadId`, so the grouping
 * is a fold over a page we have paid for either way.
 *
 * The tenant key is fetched for the same reason the approvals route
 * fetches it — composing seals with the PUBLIC half and needs no unlock,
 * and `currentKekVersion` is what lets the thread view say "sealed under
 * an older key" instead of reporting a crypto failure.
 */
export const Route = createFileRoute('/vault/messages')({
  loader: async () =>
    withSessionRedirect(async () => {
      const tree = await fetchHierarchy()
      const rootId = tree.roots[0]?.id ?? ''
      if (rootId === '')
        return {
          rootId,
          messages: [],
          principals: [],
          viewerPrincipalId: null,
          tenantPubkey: null,
          currentKekVersion: null,
        }
      const [tenantKey, principals, messages, userId] = await Promise.all([
        getTenantKey(rootId),
        listPrincipals(rootId),
        // `includeConsumed` is left at the operator plane's default of
        // TRUE: this is the tenant's correspondence, and a console that
        // silently hid the handled half would be a worse answer than a
        // longer list. The bell asks the narrower question.
        listVaultMessages(rootId, { limit: 200 }),
        sessionUserId(),
      ])
      return {
        rootId,
        messages,
        // `agent_defaults` is a template rather than an addressable
        // principal — the server 404s it as a recipient — so it is
        // filtered out of the compose list here rather than offered and
        // then refused.
        principals: principals.filter(
          (principal) => principal.kind !== 'agent_defaults',
        ),
        viewerPrincipalId: viewerPrincipalFrom(principals, userId),
        tenantPubkey: tenantKey.tenantPubkey,
        currentKekVersion: tenantKey.currentKekVersion,
      }
    }),
  component: VaultMessagesRoute,
})

/** Slower than the approvals queue's ten seconds: a message is
 * correspondence, and nobody is blocked on the console noticing one
 * within ten seconds. The bell polls at thirty (D11) and is the thing
 * that actually tells an operator mail has arrived. */
const REFRESH_MS = 30_000

function VaultMessagesRoute() {
  const data = Route.useLoaderData()
  const router = useRouter()
  useAutoRefresh(REFRESH_MS)

  return (
    <VaultMessages
      rootId={data.rootId}
      threads={groupIntoThreads(data.messages, data.viewerPrincipalId)}
      recipients={data.principals}
      viewerPrincipalId={data.viewerPrincipalId}
      tenantPubkey={data.tenantPubkey}
      currentKekVersion={data.currentKekVersion}
      onChanged={async () => {
        await router.invalidate()
      }}
    />
  )
}
